import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { PhoneNumber } from '@app/core/interfaces/c5/phone-number';
import { PhoneNumberDirection } from '@app/core/interfaces/c5/phone-number-direction';
import { SearchEnterprisePhoneNumbersParameters } from '@app/core/interfaces/search-enterprise-phone-numbers-parameters';
import { EnterpriseRepository } from '@app/core/repositories/enterprise.repository';
import { ErrorTransformerService } from '@app/shared/services/error-transformer.service';
import { PortalError } from '@app/utils/error/portal-error';
import { wrapError } from '@app/utils/rxjs';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EnterprisePhoneNumberService {
  constructor(
    private enterpriseRepository: EnterpriseRepository,
    private errorTransformerService: ErrorTransformerService
  ) {}

  assertInternalNumberAvailability(
    internal?: string,
    cc?: string,
    ndc?: string,
    sn?: string
  ): Observable<ValidationErrors> {
    let errors: Observable<ValidationErrors> = of(null);

    if (internal || cc || ndc || sn) {
      errors = timer(250).pipe(
        switchMap(() => {
          return this.search({
            cc,
            ndc,
            sn,
            internal,
          }).pipe(
            map((phoneNumbers) => {
              let errors: ValidationErrors = null;

              const phoneNumber = phoneNumbers.find(
                (phoneNumber) =>
                  phoneNumber.dir === PhoneNumberDirection.IN_OUT ||
                  phoneNumber.dir === PhoneNumberDirection.IN
              );

              if (phoneNumber !== undefined) {
                errors = {
                  custom: 'users.phoneNumbers.add.alreadyInUse',
                };
              }

              return errors;
            }),
            catchError((error: PortalError) => {
              const description = this.errorTransformerService.describeError(error);

              return of({
                custom: description.details?.exception ?? description.title,
              });
            })
          );
        }),
        wrapError('enterprise-phone-number-service:assert-internal-availability')
      );
    }

    return errors;
  }

  search(parameters: SearchEnterprisePhoneNumbersParameters): Observable<PhoneNumber[]> {
    return this.enterpriseRepository.searchPhoneNumbers(parameters);
  }
}
